import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const CART_STORAGE_KEY = "yulu_cart_v1";

// WhatsApp number — change here to your real number (international format, no +)
export const WHATSAPP_NUMBER = "50375980008";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {
      // ignore
    }
    return [];
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore quota errors
    }
  }, [items]);

  const addItem = (product) => {
    setItems((prev) => {
      const found = prev.find((p) => p.id === product.id);
      if (found) {
        return prev.map((p) =>
          p.id === product.id ? { ...p, qty: p.qty + 1 } : p
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
    setOpen(true);
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((p) => p.id !== id));

  const removeAllItems = () => {
    setItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const incrementItem = (id) =>
    setItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, qty: p.qty + 1 } : p))
    );

  const decrementItem = (id) =>
    setItems((prev) =>
      prev
        .map((p) => (p.id === id ? { ...p, qty: p.qty - 1 } : p))
        .filter((p) => p.qty > 0)
    );

  const subtotal = useMemo(
    () => items.reduce((acc, p) => acc + p.price_usd * p.qty, 0),
    [items]
  );

  const itemCount = useMemo(
    () => items.reduce((acc, p) => acc + p.qty, 0),
    [items]
  );

  const buildWhatsAppLink = (lang = "es") => {
    if (items.length === 0) return `https://wa.me/${WHATSAPP_NUMBER}`;
    const greeting =
      lang === "es"
        ? "Hola! Me gustaría hacer este pedido de cacao ceremonial:"
        : "Hello! I would like to place this ceremonial cacao order:";
    const itemsText = items
      .map((p) => {
        const name = lang === "es" ? p.name_es : p.name_en;
        return `• ${name} ${p.weight} × ${p.qty} — $${(
          p.price_usd * p.qty
        ).toFixed(2)} USD`;
      })
      .join("\n");
    const total =
      lang === "es"
        ? `\n\nTotal: $${subtotal.toFixed(2)} USD`
        : `\n\nTotal: $${subtotal.toFixed(2)} USD`;
    const text = encodeURIComponent(`${greeting}\n\n${itemsText}${total}`);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  };

  const value = {
    items,
    addItem,
    removeItem,
    removeAllItems,
    incrementItem,
    decrementItem,
    subtotal,
    itemCount,
    open,
    setOpen,
    buildWhatsAppLink,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
